import React, { useState, useEffect } from 'react'
import { Card, Row, Col, Button, Badge, Spinner } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import Layout from '../components/Layout'
import { supabase } from '../services/supabase'

export default function Dashboard({ user }) {
  const navigate = useNavigate()
  const [todaySessions, setTodaySessions] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchTodaySessions()
    const interval = setInterval(() => { fetchTodaySessions() }, 30000)
    return () => clearInterval(interval)
  }, [])

  const fetchTodaySessions = async () => {
    setLoading(true)
    try {
      const now = new Date()
      const luxFormatter = new Intl.DateTimeFormat('fr-CA', {
        timeZone: 'Europe/Luxembourg',
        year: 'numeric', month: '2-digit', day: '2-digit',
      })
      const luxDate = luxFormatter.format(now)

      const isDST = (date) => {
        const jan = new Date(date.getFullYear(), 0, 1)
        const jul = new Date(date.getFullYear(), 6, 1)
        return Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset()) !== date.getTimezoneOffset()
      }
      const offset = isDST(now) ? '+02:00' : '+01:00'
      const startLux = new Date(`${luxDate}T00:00:00${offset}`).toISOString()
      const endLux   = new Date(`${luxDate}T23:59:59${offset}`).toISOString()

      const { data, error } = await supabase
        .from('sessions')
        .select(`
          id, date, course_type_id,
          course_types (name, location, day_of_week, start_time, end_time, description)
        `)
        .gte('date', startLux)
        .lte('date', endLux)
        .order('date', { ascending: true })

      if (error) { setTodaySessions([]); return }
      setTodaySessions(data || [])
    } catch (err) {
      console.error('Erreur dans fetchTodaySessions:', err)
      setTodaySessions([])
    } finally {
      setLoading(false)
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // ✅ LOGIQUE CORRIGÉE
  //
  // Problème précédent :
  //   - start = session.date (ex: 2026-02-25T00:00:00Z = minuit)
  //   - end   = start + durée calculée → donc end = minuit + 2h = 02:00 !!!
  //   - Résultat : le cours "Boxe Thai 16h-18h" était marqué "À venir" toute la journée
  //
  // Solution :
  //   - On reconstruit start ET end à partir de la DATE du jour + start_time/end_time
  //   - On utilise le fuseau Europe/Luxembourg
  //   - now < end (strict) → à 18h00 pile le cours est "Terminé"
  // ─────────────────────────────────────────────────────────────────────────
  const getSessionStatus = (session) => {
    const now = new Date()
    const ct  = session.course_types

    // Extraire la date du jour en fuseau Luxembourg
    const luxFormatter = new Intl.DateTimeFormat('fr-CA', {
      timeZone: 'Europe/Luxembourg',
      year: 'numeric', month: '2-digit', day: '2-digit',
    })
    const luxDate = luxFormatter.format(now) // ex: "2026-02-25"

    const isDST = (date) => {
      const jan = new Date(date.getFullYear(), 0, 1)
      const jul = new Date(date.getFullYear(), 6, 1)
      return Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset()) !== date.getTimezoneOffset()
    }
    const offset = isDST(now) ? '+02:00' : '+01:00'

    // ✅ Construire start à partir de la date + start_time du cours
    let start, end

    if (ct?.start_time && ct?.end_time) {
      const startStr = String(ct.start_time).substring(0, 5) // "16:00"
      const endStr   = String(ct.end_time).substring(0, 5)   // "18:00"
      start = new Date(`${luxDate}T${startStr}:00${offset}`)
      end   = new Date(`${luxDate}T${endStr}:00${offset}`)
    } else {
      // Fallback : utiliser session.date + 90 min
      start = new Date(session.date)
      end   = new Date(start.getTime() + 90 * 60000)
    }

    if (now < start)              return { text: 'À venir',  variant: 'info' }
    if (now >= start && now < end) return { text: 'En cours', variant: 'success' }
    return                               { text: 'Terminée', variant: 'secondary' }
    //                                                   ↑
    //                          now < end (strict) → à 18h00 pile = Terminée ✅
  }

  const formatTimeRange = (session) => {
    const ct = session.course_types
    const startTime = ct?.start_time
      ? String(ct.start_time).substring(0, 5)
      : new Date(session.date).toLocaleTimeString('fr-FR', {
          timeZone: 'Europe/Luxembourg', hour: '2-digit', minute: '2-digit', hour12: false
        })
    const endTime = ct?.end_time ? String(ct.end_time).substring(0, 5) : '?'
    return `${startTime} - ${endTime}`
  }

  const activeSessions = todaySessions.filter(s => getSessionStatus(s).text !== 'Terminée')

  return (
    <Layout user={user}>
      <div className="text-center mb-5">
        <div className="mb-3" style={{ fontSize: '4rem' }}>🥊</div>
        <h1 className="display-5 fw-bold text-primary">Street Sport Luxembourg</h1>
        <p className="lead text-muted">Tableau de bord et rapports des activités</p>
      </div>

      {/* Sessions du jour */}
      <Card className="mb-4 shadow-sm">
        <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
          <h5 className="mb-0">📅 Sessions d'aujourd'hui ({activeSessions.length})</h5>
          <Button variant="outline-light" size="sm" onClick={fetchTodaySessions}>Rafraîchir</Button>
        </Card.Header>

        <Card.Body>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Chargement des sessions...</p>
            </div>
          ) : activeSessions.length > 0 ? (
            <Row className="g-3">
              {activeSessions.map((session) => {
                const status = getSessionStatus(session)
                const ct = session.course_types

                return (
                  <Col md={6} lg={4} key={session.id}>
                    <Card className="h-100 border-start border-4 border-success shadow-sm">
                      <Card.Body>
                        {/* Titre + statut */}
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <h6 className="mb-0 fw-bold">{ct?.name ?? 'Session'}</h6>
                          <Badge bg={status.variant}>{status.text}</Badge>
                        </div>

                        {/* Infos session */}
                        <div className="small text-muted mb-3">
                          <div className="mb-1">🕐 {formatTimeRange(session)}</div>
                          <div className="mb-1">📍 {ct?.location ?? '-'}</div>
                          <div>📅 {ct?.day_of_week ?? '-'}</div>
                        </div>

                        {/* ── Présence Membres ── */}
                        <div className="mb-2">
                          <div className="text-muted small fw-semibold mb-1 text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>
                            👥 Membres
                          </div>
                          <div className="d-flex gap-2">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="w-100"
                              onClick={() => navigate(`/checkin/name/${session.id}`)}
                            >
                              👤 Par Nom
                            </Button>
                            <Button
                              variant="outline-success"
                              size="sm"
                              className="w-100"
                              onClick={() => navigate(`/checkin/qr/${session.id}`)}
                            >
                              📷 QR Code
                            </Button>
                          </div>
                        </div>

                        {/* ── Signature Coachs ── */}
                        <div>
                          <div className="text-muted small fw-semibold mb-1 text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>
                            🏅 Coachs
                          </div>
                          <div className="d-flex gap-2">
                            <Button
                              variant="outline-warning"
                              size="sm"
                              className="w-100"
                              onClick={() => navigate(`/checkin/coach-name/${session.id}`)}
                            >
                              👤 Par Nom
                            </Button>
                            <Button
                              size="sm"
                              className="w-100"
                              style={{ borderColor: '#ca8a04', color: '#ca8a04', background: 'transparent' }}
                              onMouseEnter={e => { e.currentTarget.style.background = '#ca8a04'; e.currentTarget.style.color = '#fff' }}
                              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#ca8a04' }}
                              onClick={() => navigate(`/checkin/coach-qr/${session.id}`)}
                            >
                              📷 QR Code
                            </Button>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                )
              })}
            </Row>
          ) : (
            <div className="text-center py-4 text-muted">
              <div style={{ fontSize: '3rem' }}>
                {todaySessions.length > 0 ? '✅' : '📭'}
              </div>
              <p className="mb-0">
                {todaySessions.length > 0
                  ? 'Toutes les sessions du jour sont terminées !'
                  : "Aucune session prévue aujourd'hui"}
              </p>
              <small>
                {todaySessions.length > 0
                  ? 'Revenez demain pour les prochaines sessions.'
                  : 'Les sessions apparaîtront quand le cron les créera.'}
              </small>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Lien programme complet */}
      <Card className="shadow-sm">
        <Card.Body className="d-flex justify-content-between align-items-center">
          <div>
            <h6 className="mb-1">📅 Voir le programme complet</h6>
            <div className="text-muted small">Cours hebdomadaires</div>
          </div>
          <Button variant="primary" onClick={() => navigate('/courses')}>Cours</Button>
        </Card.Body>
      </Card>
    </Layout>
  )
}